import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { QrCode, Wifi, WifiOff, Phone, LogOut, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import DirectCall from '../utils/DirectCall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MobileHome = () => {
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const storedSession = localStorage.getItem('mobile_session_id');
    if (storedSession) {
      setSessionId(storedSession);
      setIsConnected(true);
      connectWebSocket(storedSession);
    } else {
      setConnecting(false);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = (session) => {
    setConnecting(true);
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/api/ws/mobile/${session}`;
    
    console.log('📱 Connecting to WebSocket:', fullUrl);
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ Mobile WebSocket connected');
      setSocketConnected(true);
      setConnecting(false);
      toast.success('Connected to CRM!');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📩 Received:', data);
        
        if (data.type === 'dial_number') {
          console.log('📞 Incoming call command:', data);
          setIncomingCall(data);
          toast.info(`Calling ${data.customer_name}...`);
          DirectCall.call(data.phone).then(result => {
            console.log('Call initiated:', result);
          });
          setTimeout(() => setIncomingCall(null), 3000);
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.log('❌ Mobile WebSocket disconnected');
      setSocketConnected(false);
      setConnecting(false);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnecting(false);
    };
  };

  const handleScanQR = () => {
    navigate('/mobile-scanner');
  };

  const handleDisconnect = () => {
    localStorage.removeItem('mobile_session_id');
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsConnected(false);
    setSessionId(null);
    setSocketConnected(false);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Compact Header */}
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
            <Phone className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-base font-semibold">Shri Kripa</h1>
            <p className="text-xs text-blue-100">Mobile Bridge</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
          socketConnected ? 'bg-green-500/30' : connecting ? 'bg-yellow-500/30' : 'bg-red-500/30'
        }`}>
          {connecting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : socketConnected ? (
            <Wifi className="h-3 w-3" />
          ) : (
            <WifiOff className="h-3 w-3" />
          )}
          <span>{connecting ? 'Connecting...' : socketConnected ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {/* Incoming Call Alert */}
      {incomingCall && (
        <div className="mx-4 mt-3 p-3 bg-green-500 text-white rounded-lg animate-pulse">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            <div>
              <p className="font-medium text-sm">Calling {incomingCall.customer_name}</p>
              <p className="text-xs opacity-90">{incomingCall.phone}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4">
        {!isConnected ? (
          /* Not Connected - Show Scan QR Card */
          <Card className="bg-white p-4 shadow-sm">
            <div className="text-center">
              <div className="h-16 w-16 mx-auto mb-3 rounded-full bg-blue-50 flex items-center justify-center">
                <QrCode className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">
                Connect to CRM
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Scan QR code from desktop to pair
              </p>
              
              <Button 
                onClick={handleScanQR}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="scan-qr-button"
              >
                <QrCode className="h-5 w-5 mr-2" />
                Scan QR Code
              </Button>
            </div>

            {/* Quick Instructions */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-700 mb-2">How to connect:</p>
              <ol className="text-xs text-slate-500 space-y-1">
                <li>1. Open CRM on desktop</li>
                <li>2. Click "Pair Mobile Device"</li>
                <li>3. Scan the QR code</li>
              </ol>
            </div>
          </Card>
        ) : (
          /* Connected - Show Status Card */
          <Card className="bg-white p-4 shadow-sm">
            <div className="text-center">
              <div className={`h-14 w-14 mx-auto mb-3 rounded-full flex items-center justify-center ${
                socketConnected ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                {socketConnected ? (
                  <Wifi className="h-7 w-7 text-green-600" />
                ) : connecting ? (
                  <Loader2 className="h-7 w-7 text-yellow-600 animate-spin" />
                ) : (
                  <WifiOff className="h-7 w-7 text-yellow-600" />
                )}
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">
                {socketConnected ? 'Connected' : connecting ? 'Connecting...' : 'Disconnected'}
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                {socketConnected 
                  ? 'Ready to receive calls from CRM'
                  : 'Trying to connect...'}
              </p>

              {socketConnected && (
                <div className="bg-green-50 p-3 rounded-lg mb-4">
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm font-medium">Click-to-Call Active</span>
                  </div>
                </div>
              )}

              {!socketConnected && !connecting && (
                <Button 
                  onClick={() => sessionId && connectWebSocket(sessionId)}
                  className="w-full mb-3 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Reconnect
                </Button>
              )}

              <Button 
                onClick={handleDisconnect}
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
                data-testid="disconnect-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </Card>
        )}

        {/* Features Section - Compact */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Card className="bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center">
                <Phone className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Direct Call</p>
                <p className="text-xs text-slate-500">No dialpad</p>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-green-50 flex items-center justify-center">
                <MessageCircle className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">WhatsApp</p>
                <p className="text-xs text-slate-500">Quick send</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-3 text-center bg-slate-100">
        <p className="text-xs text-slate-400">
          Shri Kripa • Mobile Bridge v1.0
        </p>
      </div>
    </div>
  );
};

export default MobileHome;
